import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PropertyCard from '@/components/PropertyCard';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ShieldCheck, MapPin, Award, Building2, FileText, Phone, MessageCircle, Mail, Briefcase, Sparkles, Send, Check } from 'lucide-react';

const BrokerProfile = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiry, setEnquiry] = useState({ name: '', mobile: '', email: '', budget_min: '', budget_max: '', preferred_location: '', property_requirement: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { load(); }, [slug]);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/broker/${slug}`);
      setData(data);
    } catch (e) {
      toast.error('Broker not found');
    } finally {
      setLoading(false);
    }
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!data) return;
    setSubmitting(true);
    try {
      const payload = {
        name: enquiry.name,
        mobile: enquiry.mobile,
        email: enquiry.email || null,
        preferred_location: enquiry.preferred_location || null,
        property_requirement: enquiry.property_requirement || null,
        message: enquiry.message || null,
        broker_id: data.broker.user_id,
        source: 'broker_profile'
      };
      if (enquiry.budget_min) payload.budget_min = parseFloat(enquiry.budget_min);
      if (enquiry.budget_max) payload.budget_max = parseFloat(enquiry.budget_max);
      await api.post('/leads', payload);
      setSubmitted(true);
      toast.success('Enquiry sent! Broker will contact you shortly.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto p-8 text-center text-slate-500">Loading...</div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-7xl mx-auto p-16 text-center">
        <div className="text-2xl font-bold text-slate-900">Broker not found</div>
        <p className="text-slate-500 mt-2">This broker profile does not exist or is not yet approved.</p>
        <Link to="/search"><Button className="mt-4 bg-[#F97316] hover:bg-[#EA580C]">Browse Properties</Button></Link>
      </div>
      <Footer />
    </div>
  );

  const { broker, projects, stats } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* HERO */}
      <section className="bg-gradient-to-br from-[#0F2C59] to-[#1E3A6F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Avatar */}
            <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-white text-[#0F2C59] flex items-center justify-center font-black text-4xl flex-shrink-0 shadow-lg" data-testid="broker-avatar">
              {broker.full_name?.charAt(0)?.toUpperCase()}
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight" data-testid="broker-name">{broker.full_name}</h1>
                <Badge className="bg-emerald-500 hover:bg-emerald-500 gap-1"><ShieldCheck size={14}/> RERA Verified</Badge>
              </div>
              {broker.agency_name && (
                <div className="text-lg text-slate-200 flex items-center gap-2" data-testid="broker-agency">
                  <Briefcase size={16} className="text-[#F97316]"/> {broker.agency_name}
                </div>
              )}
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-300 flex-wrap">
                <span className="flex items-center gap-1"><Award size={14} className="text-[#F97316]"/> {broker.years_of_experience}+ Years Experience</span>
                <span className="flex items-center gap-1"><MapPin size={14} className="text-[#F97316]"/> {broker.primary_city}</span>
                {broker.rera_number && <span className="font-mono text-xs bg-white/10 px-2 py-0.5 rounded">RERA: {broker.rera_number}</span>}
              </div>

              {/* Specializations */}
              {broker.specializations?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {broker.specializations.map((s, i) => (
                    <span key={i} className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs font-medium">{s}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Contact */}
            <div className="w-full md:w-auto flex md:flex-col gap-2">
              {broker.whatsapp_number && (
                <a href={`https://wa.me/${broker.whatsapp_number.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 rounded-md text-sm font-semibold" data-testid="broker-whatsapp-btn">
                  <MessageCircle size={16}/> WhatsApp
                </a>
              )}
              <a href={`tel:${broker.whatsapp_number || ''}`} className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#F97316] hover:bg-[#EA580C] rounded-md text-sm font-semibold" data-testid="broker-call-btn">
                <Phone size={16}/> Call
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 md:-mt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Building2, label: 'Projects', value: stats.total_projects, color: 'text-blue-600 bg-blue-50' },
            { icon: FileText, label: 'Landing Pages', value: stats.total_landing_pages, color: 'text-[#F97316] bg-orange-50' },
            { icon: MapPin, label: 'Cities Covered', value: stats.cities_covered.length, color: 'text-emerald-600 bg-emerald-50' },
            { icon: Award, label: 'Years Exp.', value: stats.years_of_experience, color: 'text-purple-600 bg-purple-50' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-3" data-testid={`broker-stat-${s.label.toLowerCase().replace(/\s+/g, '-').replace('.', '')}`}>
              <div className={`w-10 h-10 rounded-md ${s.color} flex items-center justify-center flex-shrink-0`}>
                <s.icon size={18}/>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{s.label}</div>
                <div className="font-display font-extrabold text-xl text-[#0F2C59]">{s.value}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT — About + Projects */}
          <div className="lg:col-span-2 space-y-6">
            {broker.bio && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="font-display font-bold text-xl text-[#0F2C59] mb-3 flex items-center gap-2">
                  <Sparkles size={18} className="text-[#F97316]"/> About {broker.full_name?.split(' ')[0]}
                </h2>
                <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-testid="broker-bio">{broker.bio}</p>
              </div>
            )}

            {stats.cities_covered.length > 0 && (
              <div className="bg-white rounded-lg border border-slate-200 p-6">
                <h2 className="font-display font-bold text-xl text-[#0F2C59] mb-3">Operating Cities</h2>
                <div className="flex flex-wrap gap-2">
                  {stats.cities_covered.map((c, i) => (
                    <Link key={i} to={`/search?city=${c}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-[#0F2C59] hover:text-white rounded-md text-sm font-medium" data-testid={`operating-city-${c}`}>
                      <MapPin size={12}/> {c}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Projects */}
            <div>
              <div className="flex items-end justify-between mb-4">
                <h2 className="font-display font-bold text-2xl text-[#0F2C59]">Published Projects</h2>
                <span className="text-sm text-slate-500">{projects.length} listing{projects.length !== 1 ? 's' : ''}</span>
              </div>

              {projects.length === 0 ? (
                <div className="bg-white rounded-lg border border-dashed border-slate-300 p-10 text-center">
                  <Building2 size={36} className="mx-auto text-slate-300 mb-2"/>
                  <div className="text-slate-500 text-sm">No published projects yet</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {projects.map(p => <PropertyCard key={p.id} project={p} />)}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — Contact Card */}
          <aside className="lg:col-span-1 space-y-4">
            {/* Direct Enquiry Form */}
            <div className="bg-white rounded-lg border-2 border-[#F97316] p-6 sticky top-20 shadow-md">
              {submitted ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                    <Check size={26} className="text-emerald-600"/>
                  </div>
                  <h3 className="font-display font-bold text-lg text-[#0F2C59]">Enquiry Sent!</h3>
                  <p className="text-sm text-slate-600 mt-2">{broker.full_name?.split(' ')[0]} will contact you shortly.</p>
                  <button onClick={() => { setSubmitted(false); setEnquiry({ name: '', mobile: '', email: '', budget_min: '', budget_max: '', preferred_location: '', property_requirement: '', message: '' }); }} className="text-xs text-[#F97316] font-semibold mt-3 hover:underline" data-testid="reset-enquiry-btn">Send another enquiry</button>
                </div>
              ) : (
                <>
                  <h3 className="font-display font-bold text-lg text-[#0F2C59] flex items-center gap-2"><Send size={16} className="text-[#F97316]"/> Contact {broker.full_name?.split(' ')[0]}</h3>
                  <p className="text-xs text-slate-500 mt-1">Discuss your property requirements directly</p>
                  <form onSubmit={submitEnquiry} className="mt-4 space-y-3">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Name *</Label>
                      <Input value={enquiry.name} onChange={(e) => setEnquiry({...enquiry, name: e.target.value})} required data-testid="direct-enquiry-name" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Mobile *</Label>
                      <Input type="tel" value={enquiry.mobile} onChange={(e) => setEnquiry({...enquiry, mobile: e.target.value})} required data-testid="direct-enquiry-mobile" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
                      <Input type="email" value={enquiry.email} onChange={(e) => setEnquiry({...enquiry, email: e.target.value})} data-testid="direct-enquiry-email" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Min ₹</Label>
                        <Input type="number" value={enquiry.budget_min} onChange={(e) => setEnquiry({...enquiry, budget_min: e.target.value})} placeholder="50 L" data-testid="direct-enquiry-budget-min" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Max ₹</Label>
                        <Input type="number" value={enquiry.budget_max} onChange={(e) => setEnquiry({...enquiry, budget_max: e.target.value})} placeholder="1 Cr" data-testid="direct-enquiry-budget-max" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Preferred Location</Label>
                      <Input value={enquiry.preferred_location} onChange={(e) => setEnquiry({...enquiry, preferred_location: e.target.value})} placeholder="Whitefield, Bengaluru" data-testid="direct-enquiry-location" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Requirement</Label>
                      <Input value={enquiry.property_requirement} onChange={(e) => setEnquiry({...enquiry, property_requirement: e.target.value})} placeholder="3BHK ready-to-move" data-testid="direct-enquiry-requirement" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Message</Label>
                      <Textarea value={enquiry.message} onChange={(e) => setEnquiry({...enquiry, message: e.target.value})} rows={3} placeholder="Tell the broker what you're looking for..." data-testid="direct-enquiry-message" />
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-[#F97316] hover:bg-[#EA580C] font-bold h-11" data-testid="direct-enquiry-submit">
                      {submitting ? 'Sending...' : 'Send Enquiry'}
                    </Button>
                  </form>
                  <div className="mt-4 pt-4 border-t border-slate-200 text-xs text-slate-500 text-center flex items-center justify-center gap-1">
                    <ShieldCheck size={12} className="text-emerald-600"/> Your data is safe & never shared
                  </div>
                </>
              )}
            </div>

            {/* Quick Contact Card */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <h4 className="font-semibold text-sm text-[#0F2C59] mb-3">Quick Contact</h4>
              <div className="space-y-2">
                {broker.whatsapp_number && (
                  <a href={`https://wa.me/${broker.whatsapp_number.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-2.5 bg-emerald-50 hover:bg-emerald-100 rounded-md" data-testid="contact-whatsapp">
                    <MessageCircle size={16} className="text-emerald-600"/>
                    <div className="text-sm">
                      <div className="text-xs text-slate-500">WhatsApp</div>
                      <div className="font-semibold text-slate-900">{broker.whatsapp_number}</div>
                    </div>
                  </a>
                )}
                {broker.whatsapp_number && (
                  <a href={`tel:${broker.whatsapp_number}`} className="flex items-center gap-3 p-2.5 bg-blue-50 hover:bg-blue-100 rounded-md" data-testid="contact-call">
                    <Phone size={16} className="text-blue-600"/>
                    <div className="text-sm">
                      <div className="text-xs text-slate-500">Call Now</div>
                      <div className="font-semibold text-slate-900">Instant callback</div>
                    </div>
                  </a>
                )}
                {broker.website && (
                  <a href={broker.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-2.5 bg-slate-50 hover:bg-slate-100 rounded-md">
                    <Mail size={16} className="text-slate-600"/>
                    <div className="text-sm min-w-0">
                      <div className="text-xs text-slate-500">Website</div>
                      <div className="font-semibold text-slate-900 truncate">{broker.website}</div>
                    </div>
                  </a>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BrokerProfile;
